import { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { TopNav } from './TopNav'
import { FiMenu, FiX } from 'react-icons/fi'
import { useAuthStore } from '../../store'
import { useThemeConfig } from '../../theme/ThemeConfigContext'
import { SkipNavLink, CookieConsentBanner, OfflineBanner } from '../common'
import { cn } from '../../lib/utils'
import { DebugPanel } from '../debug/DebugPanel'
import { DemoModeBar } from '../demo/DemoModeBar'
import { ImpersonationBar } from '../admin/ImpersonationBar'
import { CartDrawer } from './CartDrawer'
import { CommandPalette } from '../command-palette'
import { ToastContainer } from '../ui/toast'

export function AppLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const { config } = useThemeConfig()
  const navMode = config.layout.navigationMode

  const userName = user?.displayName ?? 'User'
  const userInitials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  if (navMode === 'topnav') {
    return (
      <div className="min-h-screen bg-background-secondary">
        <OfflineBanner />
        <DemoModeBar />
        <ImpersonationBar />
        <SkipNavLink />

        <TopNav
          userName={userName}
          userInitials={userInitials}
          onLogout={handleLogout}
          isMobileMenuOpen={isMobileMenuOpen}
          onToggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />

        {/* Main content area — offset by topnav height */}
        <div
          id="main-content"
          className="min-h-screen bg-background"
          style={{
            paddingTop: 'var(--theme-topnav-height, 64px)',
          }}
        >
          <div className="w-full">
            <Outlet />
          </div>
        </div>

        <CookieConsentBanner />
        <CartDrawer />
        <CommandPalette />

        <ToastContainer />
        {import.meta.env.DEV && <DebugPanel />}
      </div>
    )
  }

  // Default: sidebar layout
  return (
    <div className="min-h-screen bg-background-secondary">
      <OfflineBanner />
      <DemoModeBar />
      <ImpersonationBar />
      <SkipNavLink />

      {/* Mobile menu button */}
      <button
        className={cn(
          'flex lg:hidden fixed left-4 top-4 z-[1100] w-10 h-10 bg-sidebar text-white rounded-[var(--radius-button)] items-center justify-center cursor-pointer shadow-md'
        )}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
      </button>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          className="block lg:hidden fixed inset-0 bg-black/60 z-[999]"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - hidden on mobile unless open */}
      <div
        className={cn(
          'fixed z-[1000]',
          isMobileMenuOpen ? 'block' : 'hidden',
          'lg:block'
        )}
      >
        <Sidebar userName={userName} userInitials={userInitials} onLogout={handleLogout} />
      </div>

      {/* Main content area */}
      <div
        id="main-content"
        className="min-h-screen bg-background"
        style={{
          marginLeft: 0,
        }}
      >
        <style>{`@media (min-width: 1024px) { #main-content { margin-left: calc(var(--theme-sidebar-width, 200px) + var(--theme-sidebar-inset, 0px)) !important; } }`}</style>
        <div className="w-full">
          <Outlet />
        </div>
      </div>

      <CookieConsentBanner />
      <CartDrawer />
      <CommandPalette />
      <ToastContainer />
      {import.meta.env.DEV && <DebugPanel />}
    </div>
  )
}
